// DocumentVerification.js - Fixed version with proper exports
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  doc, 
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../configs/firebase';
import { 
  FaFileUpload, 
  FaCheck, 
  FaExclamationTriangle,
  FaInfoCircle,
  FaGlobe
} from 'react-icons/fa';

const DocumentVerification = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employerData, setEmployerData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

  const validationSchema = Yup.object({
    businessLicenseUrl: Yup.string()
      .matches(urlRegex, 'Invalid URL format')
      .required('Business license document URL is required'),
    
    taxDocumentUrl: Yup.string()
      .matches(urlRegex, 'Invalid URL format')
      .required('Tax document URL is required'),
    
    companyProfileUrl: Yup.string()
      .matches(urlRegex, 'Invalid URL format'),
    
    dataAccuracy: Yup.boolean()
      .oneOf([true], 'You must confirm data accuracy')
  });

  const initialValues = {
    businessLicenseUrl: '',
    taxDocumentUrl: '',
    companyProfileUrl: '',
    dataAccuracy: false
  };

  // Monitor auth state and load employer data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
      
      if (firebaseUser) {
        try {
          const employerDoc = await getDoc(doc(db, 'employers', firebaseUser.uid));
          if (employerDoc.exists()) {
            const data = employerDoc.data();
            setEmployerData(data);
            
            // If already completed or approved, redirect appropriately
            if (data.registrationPhase === 'completed' || data.verificationStatus === 'approved') {
              navigate('/employer/');
            }
          } else {
            // No employer profile found, redirect to signup
            navigate('/employer/register');
          }
        } catch (error) {
          console.error('Error loading employer data:', error);
        }
      } else {
        // Not authenticated, redirect to login
        navigate('/employer/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const submitDocuments = async (values) => {
    setSubmitting(true);
    
    try {
      console.log('📄 Submitting documents for verification...');
      
      // Update employer application
      const applicationQuery = await getDocs(
        query(
          collection(db, 'employer_applications'), 
          where('uid', '==', user.uid)
        )
      );
      
      if (!applicationQuery.empty) {
        const applicationDocId = applicationQuery.docs[0].id;
        
        // Update application with documents
        await updateDoc(doc(db, 'employer_applications', applicationDocId), {
          documentUrls: {
            businessLicenseUrl: values.businessLicenseUrl,
            taxDocumentUrl: values.taxDocumentUrl,
            companyProfileUrl: values.companyProfileUrl || ''
          },
          documentsProvided: {
            businessLicense: !!values.businessLicenseUrl,
            taxDocument: !!values.taxDocumentUrl,
            companyProfile: !!values.companyProfileUrl
          },
          applicationStatus: 'submitted',
          documentsSubmittedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      // Update employer profile
      await updateDoc(doc(db, 'employers', user.uid), {
        applicationStatus: 'submitted',
        registrationPhase: 'under_review',
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Documents submitted successfully');
      alert('Documents submitted successfully! Your application is now under review. You will receive an email once your application is processed.');
      navigate('/employer/application-submitted');
      
    } catch (error) {
      console.error('❌ Error submitting documents:', error);
      alert('Error submitting documents: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!employerData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 flex items-center justify-center">
        <div className="text-white text-xl">Loading employer data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 p-4 md:p-10">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Document Verification Required</h2>
          <p className="text-gray-700 text-lg">
            Welcome, <strong>{employerData.companyName}</strong>! 
            Please provide the required documents to complete your registration.
          </p>
        </div>

        {/* Document Upload Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={submitDocuments}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form>
                <div className="space-y-6">
                  {/* Document Guidelines */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FaInfoCircle className="mr-2" />
                      Document Guidelines
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Ensure documents are publicly accessible or shared with view permissions</li>
                      <li>• Use Google Drive, Dropbox, OneDrive, or your company website</li>
                      <li>• Documents should be in PDF, JPG, or PNG format</li>
                      <li>• Make sure the links will remain active during the verification process</li>
                      <li>• Documents should be clear, legible, and official</li>
                    </ul>
                  </div>

                  {/* Document URLs Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                      <FaGlobe className="mr-2" />
                      Required Document URLs
                    </h4>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Business License/Registration Certificate URL *
                        </label>
                        <p className="text-sm text-gray-600 mb-3">
                          Provide a direct link to your business registration or license document
                        </p>
                        <Field
                          name="businessLicenseUrl"
                          type="url"
                          placeholder="https://drive.google.com/file/d/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <ErrorMessage name="businessLicenseUrl" component="div" className="text-red-500 text-sm mt-1" />
                        
                        {values.businessLicenseUrl && (
                          <div className="mt-2 flex items-center text-green-600 text-sm">
                            <FaCheck className="mr-2" />
                            <a 
                              href={values.businessLicenseUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              View Document
                            </a>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tax Document/EIN Certificate URL *
                        </label>
                        <p className="text-sm text-gray-600 mb-3">
                          Provide a direct link to your tax identification document or EIN certificate
                        </p>
                        <Field
                          name="taxDocumentUrl"
                          type="url"
                          placeholder="https://drive.google.com/file/d/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <ErrorMessage name="taxDocumentUrl" component="div" className="text-red-500 text-sm mt-1" />
                        
                        {values.taxDocumentUrl && (
                          <div className="mt-2 flex items-center text-green-600 text-sm">
                            <FaCheck className="mr-2" />
                            <a 
                              href={values.taxDocumentUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              View Document
                            </a>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Profile/Brochure URL (Optional)
                        </label>
                        <p className="text-sm text-gray-600 mb-3">
                          Provide a link to your company brochure, profile, or additional documentation
                        </p>
                        <Field
                          name="companyProfileUrl"
                          type="url"
                          placeholder="https://yourcompany.com/about/profile.pdf"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <ErrorMessage name="companyProfileUrl" component="div" className="text-red-500 text-sm mt-1" />
                        
                        {values.companyProfileUrl && (
                          <div className="mt-2 flex items-center text-green-600 text-sm">
                            <FaCheck className="mr-2" />
                            <a 
                              href={values.companyProfileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              View Document
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Legal Agreements */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="space-y-4">
                      <label className="flex items-start space-x-3">
                        <Field
                          type="checkbox"
                          name="dataAccuracy"
                          className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          I confirm that all information and document links provided are accurate and accessible *
                        </span>
                      </label>
                      <ErrorMessage name="dataAccuracy" component="div" className="text-red-500 text-sm" />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting || !values.businessLicenseUrl || !values.taxDocumentUrl}
                    className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center text-lg"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Submitting Documents...
                      </>
                    ) : (
                      <>
                        <FaFileUpload className="mr-3" />
                        Submit Documents for Verification
                      </>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <Link to="/employer/application-submitted" className="text-gray-700 hover:underline font-medium">
              Back to Application Status
          </Link>
        </div>
      </div>
    </div>
  );
};

// Default export
export default DocumentVerification;